import { useState, useCallback, useRef, ChangeEvent, KeyboardEvent, MouseEvent, ReactElement, JSX } from 'react';
import PostalMime, { Email } from 'postal-mime';
import { useFormContext } from 'react-hook-form';
import {
    Box,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    Chip,
    Stack,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    ArrayInput,
    SimpleFormIterator,
    TextInput,
    DateTimeInput,
} from 'react-admin';
import { RichTextInput } from 'ra-input-rich-text';

function extractSignature(text = ''): { body: string, signature: string } {
    // RFC 3676: signature delimiter is exactly "-- \n" (dash dash space newline).
    // We search from the end so we split at the last occurrence, which avoids
    // picking up "-- " inside quoted replies higher up in the thread.
    const DELIMITERS: string[] = ['\n-- \n', '\n-- \r\n'];

    let delimIdx: number = -1;
    let delimLen: number = 0;
    for (const d of DELIMITERS) {
        const idx: number = text.lastIndexOf(d);
        if (idx !== -1 && idx > delimIdx) {
            delimIdx = idx;
            delimLen = d.length;
        }
    }

    if (delimIdx === -1) return { body: text.trim(), signature: '' };

    const signature: string = text.slice(delimIdx + delimLen).trim();

    // Sanity-check: reject if the candidate is empty or unreasonably long
    // (quoted replies and forwarded messages can follow a "-- " line too).
    const lines: string[] = signature.split('\n').filter( (line: string): boolean => line.trim().length > 0);
    if (lines.length === 0 || lines.length > 20) {
        return { body: text.trim(), signature: '' };
    }

    return {
        body: text.slice(0, delimIdx).trim(),
        signature,
    };
}

// The picker's accept=".eml" is only a filter — it can be overridden in the OS dialog, and it
// does not apply to drag-and-drop at all, so the check has to live here.
const isEmlFile: (file: File) => boolean = (file: File): boolean =>
    (file?.name ?? '').toLowerCase().endsWith('.eml') || file?.type === 'message/rfc822';

const formatFileSize: (bytes: number) => string = (bytes: number): string => {
    if (!bytes || bytes < 1024) {
        return `${bytes ?? 0} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Matches the blank entry the form is seeded with, so clearing puts the field back to a state
// that reads as "no email supplied" rather than leaving stale values behind.
const EMPTY_EMAIL: Record<string, unknown> = {
    subject: '',
    from: '',
    to: '',
    cc: '',
    replyTo: '',
    date: '',
    messageId: '',
    headers: [],
    body_plain: '',
    signature: '',
    body_html: '',
    attachments: [],
    domain: '',
};

function formatAddress(addr: any): any {
    if (!addr) return '';
    if (Array.isArray(addr)) return addr.map(formatAddress).filter(Boolean).join(', ');
    if (addr.group) return addr.group.map(formatAddress).join(', ');
    return addr.name ? `${addr.name} <${addr.address}>` : (addr.address ?? '');
}

function EmailDisplay({ source }: { source: string }): ReactElement {
    // The slot always holds exactly one email, so the previous ArrayInput/SimpleFormIterator
    // contributed only numbered iterator chrome plus add/remove affordances that were both
    // disabled. Addressing `${source}.0.*` directly produces the identical field paths, so the
    // payload sent to the Operator is unchanged.
    const base: string = `${source}.0`;

    // Note: the accordions must keep their children mounted when collapsed (MUI's default —
    // do not add unmountOnExit). Unmounting a react-admin input unregisters it, which would
    // silently drop that field from the submitted payload.
    return (
        <Box sx={ {
            width: '100%',
            mt: 1.5,
            // React Admin's Layout root is min-width: fit-content (ra-ui-materialui Layout.js),
            // so the page grows to fit its widest descendant rather than clipping it. Uploading
            // an EML fills these accordions with the real email, and HTML mail is built as
            // fixed-width tables — 600px by convention — which is wider than this column. The
            // collapsed accordions' overflow: hidden and the editor's overflow: auto only stop
            // that being painted; neither stops it contributing its min-content width upward,
            // so the Layout stretched and took every Grid column with it. Size containment is
            // what breaks the chain: it makes this box's width independent of its contents.
            contain: 'inline-size',
        } }>
            <Accordion defaultExpanded disableGutters>
                <AccordionSummary expandIcon={ <ExpandMoreIcon /> }>
                    <Typography variant="subtitle2">Message</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <TextInput source={ `${base}.domain` } label="Email Sending Domain" fullWidth />
                    <DateTimeInput source={ `${base}.date` } label="Date" fullWidth />
                    <TextInput source={ `${base}.from` } label="From" fullWidth />
                    <TextInput source={ `${base}.to` } label="To" fullWidth />
                    <TextInput source={ `${base}.subject` } label="Subject" fullWidth />
                </AccordionDetails>
            </Accordion>

            <Accordion disableGutters>
                <AccordionSummary expandIcon={ <ExpandMoreIcon /> }>
                    <Typography variant="subtitle2">Body (Plain)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <TextInput source={ `${base}.body_plain` } label="Body (Plain)" multiline rows={ 10 } fullWidth />
                </AccordionDetails>
            </Accordion>

            <Accordion disableGutters>
                <AccordionSummary expandIcon={ <ExpandMoreIcon /> }>
                    <Typography variant="subtitle2">Body (HTML)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <RichTextInput source={ `${base}.body_html` } label="Body (HTML)" fullWidth sx={ {
                        '& .RaRichTextInput-editorContent': {
                            height: '300px',
                            '& .tiptap': {
                                height: '300px',
                                overflow: 'auto'
                            }
                        }
                    } }/>
                </AccordionDetails>
            </Accordion>

            <Accordion disableGutters>
                <AccordionSummary expandIcon={ <ExpandMoreIcon /> }>
                    <Typography variant="subtitle2">Signature</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <TextInput source={ `${base}.signature` } label="Signature" multiline rows={ 10 } fullWidth />
                </AccordionDetails>
            </Accordion>

            <Accordion disableGutters>
                <AccordionSummary expandIcon={ <ExpandMoreIcon /> }>
                    <Typography variant="subtitle2">Headers</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <ArrayInput source={ `${base}.headers` } label={ false }>
                        <SimpleFormIterator disableReordering>
                            <TextInput source="key" fullWidth/>
                            <TextInput source="value" fullWidth/>
                        </SimpleFormIterator>
                    </ArrayInput>
                </AccordionDetails>
            </Accordion>
        </Box>
    );

}

type EmailParserUploadProps = {
    source?: string,
    // Replaces the previous handleOutboundFileName/handleInboundFileName pair: the component
    // accepted both and fired whichever was defined, which meant passing both fired both.
    // Called with '' when the file is cleared, so the caller can drop a stale name.
    onFileLoaded?: (fileName: string) => void,
};

export function EmailParserUpload({ source = 'emailData', onFileLoaded }: EmailParserUploadProps): JSX.Element {
    const { setValue } = useFormContext();

    const [loading, setLoading]   = useState<boolean>(false);
    const [error, setError]       = useState<string|null>(null);
    const [fileName, setFileName] = useState<string|null>(null);
    const [fileSize, setFileSize] = useState<number|null>(null);
    const [dragging, setDragging] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement|null>(null);

    const handleFile: (file: File) => Promise<void> = useCallback(async (file: File): Promise<void> => {
        if (!isEmlFile(file)) {
            setError(`"${file?.name}" is not an .eml file. Export the message as EML and try again.`);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const arrayBuffer: any = await file.arrayBuffer();
            const email: Email = await PostalMime.parse(arrayBuffer);
            const { body, signature } = extractSignature(email.text ?? '');
            const domain: string = email.from?.address?.split('@')?.[1] ?? '';
            let bodyString: string = body;
            if (bodyString === undefined || bodyString.length === 0) {
                // email.html is absent on messages with no HTML part, so this threw for any
                // email that had neither a text nor an HTML body.
                bodyString = (email.html ?? '').replace(/<\/?[^>]+(>|$)/g, "")
            }

            setValue(source, [
                {
                    subject:   email.subject   ?? '',
                    from:      formatAddress(email.from),
                    to:        formatAddress(email.to),
                    cc:        formatAddress(email.cc),
                    replyTo:   formatAddress(email.replyTo),
                    date:      email.date      ?? '',
                    messageId: email.messageId ?? '',
                    headers:   email.headers,   // [{ key, value }]
                    body_plain: bodyString,
                    signature: signature,
                    body_html:      email.html ?? '',
                    attachments: (email.attachments ?? []).map(a => ({
                        filename:    a.filename,
                        mimeType:    a.mimeType,
                        disposition: a.disposition,
                    })),
                    domain: domain
                }
            ], { shouldDirty: true });

            // The headers input is an ArrayInput, and useFieldArray only resyncs when it is
            // notified about its own exact name — writing the whole slot above updates form
            // state (so the payload is correct) but leaves the rendered rows stale/empty.
            // Setting the field-array path explicitly is what repaints it.
            setValue(`${source}.0.headers`, email.headers ?? [], { shouldDirty: true });

            setFileName(file.name);
            setFileSize(file.size);
            onFileLoaded?.(file.name);
        } catch (err) {
            console.error('EML parse failed', err);
            setError('Could not read this file as an email. Check it is a valid .eml export.');
        } finally {
            setLoading(false);
        }
    }, [setValue, source, onFileLoaded]);

    const handleDrop: (e: any) => void = useCallback((e:any): void => {
        e.preventDefault();
        setDragging(false);
        const file: File = e.dataTransfer.files[0];
        if (file) handleFile(file);
    }, [handleFile]);

    const openFilePicker: () => void = useCallback((): void => {
        inputRef.current?.click();
    }, []);

    // A div is not keyboard-operable by default. role="button" + tabIndex puts it in the tab
    // order, and this restores the Enter/Space activation a real button would give for free.
    const handleKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void = useCallback((e: KeyboardEvent<HTMLDivElement>): void => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openFilePicker();
        }
    }, [openFilePicker]);

    const handleClear: () => void = useCallback((): void => {
        setValue(source, [ { ...EMPTY_EMAIL } ], { shouldDirty: true });
        // Same reason as in handleFile: the header rows will not clear on their own.
        setValue(`${source}.0.headers`, [], { shouldDirty: true });
        setFileName(null);
        setFileSize(null);
        setError(null);
        onFileLoaded?.('');
        // Without this, re-selecting the same file fires no change event and the upload
        // appears to do nothing.
        if (inputRef.current !== null) {
            inputRef.current.value = '';
        }
    }, [setValue, source, onFileLoaded]);

    return (
        <Box>
            <Paper
                variant="outlined"
                role="button"
                tabIndex={ 0 }
                aria-label={ fileName ? `Replace EML file, currently ${fileName}` : 'Upload an EML file' }
                onClick={ openFilePicker }
                onKeyDown={ handleKeyDown }
                onDragOver={ ( e: any): void => { e.preventDefault(); setDragging(true); } }
                onDragLeave={ (): void => setDragging(false) }
                onDrop={handleDrop}
                sx={{
                    display: 'block',
                    p: 3,
                    textAlign: 'center',
                    border: '2px dashed',
                    borderColor: dragging ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    bgcolor: dragging ? 'action.hover' : 'transparent',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s, background-color 0.2s',
                    '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                    '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: '2px',
                    },
                }}
            >
                <input
                    ref={ inputRef }
                    type="file"
                    accept=".eml"
                    hidden
                    onChange={ ( e: ChangeEvent<HTMLInputElement>): void => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
                />

                { loading ? (
                    <Stack alignItems="center" spacing={1.5}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="text.secondary">Parsing email…</Typography>
                    </Stack>
                ) : fileName ? (
                    <Stack alignItems="center" spacing={1}>
                        <CheckCircleOutlineIcon color="success" />
                        <Chip label={ fileName } size="small" variant="outlined" />
                        <Typography variant="caption" color="text.secondary">
                            { formatFileSize(fileSize ?? 0) } — click or drop to replace
                        </Typography>
                        <Button size="small"
                                color="inherit"
                                startIcon={ <ClearIcon fontSize="small" /> }
                                // The dropzone itself opens the picker, so this must not
                                // bubble up to it.
                                onClick={ ( e: MouseEvent<HTMLButtonElement> ): void => { e.stopPropagation(); handleClear(); } }
                        >
                            Clear
                        </Button>
                    </Stack>
                ) : (
                    <Stack alignItems="center" spacing={1}>
                        <CloudUploadIcon sx={{ fontSize: 26, color: 'text.disabled' }} />
                        <Typography variant="body1" fontWeight={500}>Drop an EML file here</Typography>
                        <Typography variant="caption" color="text.secondary">or click to browse</Typography>
                    </Stack>
                )}
            </Paper>

            { error && (
                <Alert severity="error" sx={{ mt: 1.5 }} onClose={ (): void => setError(null) }>
                    {error}
                </Alert>
            ) }

            <EmailDisplay source={source} />
        </Box>
    );
}

export default EmailParserUpload;